export const PATIENT_DETAIL_TAB_KEYS = [
  'overview',
  'appointments',
  'clinical',
  'treatments',
  'treatment-progress',
  'payments',
  'documents',
  'prescriptions',
  'lab-orders',
  'contact',
  'message-deliveries',
] as const;

export type PatientDetailTabKey = (typeof PATIENT_DETAIL_TAB_KEYS)[number];

/** Tabs whose body fills leftover height (ListPage-like / master-detail). */
export const PATIENT_DETAIL_LIST_TAB_KEYS = [
  'appointments',
  'treatments',
  'treatment-progress',
  'payments',
  'documents',
  'prescriptions',
  'lab-orders',
  'contact',
  'message-deliveries',
] as const satisfies readonly PatientDetailTabKey[];

export function isPatientDetailListTab(tab: PatientDetailTabKey): boolean {
  return (PATIENT_DETAIL_LIST_TAB_KEYS as readonly PatientDetailTabKey[]).includes(tab);
}

const TAB_KEY_SET = new Set<string>(PATIENT_DETAIL_TAB_KEYS);

/** Legacy tab key; maps to payments (+ optional section=wallet for history drawer). */
export const LEGACY_PATIENT_WALLET_TAB = 'wallet';

/** Legacy tab; messaging now lives in the patient chat panel. */
export const LEGACY_PATIENT_COMMUNICATION_TAB = 'communication';

export const DEFAULT_PATIENT_DETAIL_TAB: PatientDetailTabKey = 'overview';

export function parsePatientDetailTab(raw: string | null | undefined): PatientDetailTabKey {
  if (raw === LEGACY_PATIENT_WALLET_TAB) {
    return 'payments';
  }
  if (raw === LEGACY_PATIENT_COMMUNICATION_TAB) {
    return DEFAULT_PATIENT_DETAIL_TAB;
  }
  if (raw && TAB_KEY_SET.has(raw)) {
    return raw as PatientDetailTabKey;
  }
  return DEFAULT_PATIENT_DETAIL_TAB;
}

export function patientDetailTabIndex(tab: PatientDetailTabKey): number {
  return PATIENT_DETAIL_TAB_KEYS.indexOf(tab);
}

/** Build a patient detail path that restores a specific tab after round-trips. */
export function buildPatientDetailPath(
  patientId: string,
  tab: PatientDetailTabKey = DEFAULT_PATIENT_DETAIL_TAB
): string {
  if (tab === DEFAULT_PATIENT_DETAIL_TAB) {
    return `/patients/${patientId}`;
  }
  return `/patients/${patientId}?tab=${tab}`;
}

/** Open the patient chart on the treatments tab. The selected plan is page state, not a query. */
export function buildPatientTreatmentsPath(patientId: string): string {
  return `/patients/${patientId}?tab=treatments`;
}

/** Open the patient chart with the messaging panel on a linked thread. */
export function buildPatientMessagingPath(
  patientId: string,
  conversationId?: string | null
): string {
  if (!conversationId) {
    return `/patients/${patientId}`;
  }
  return `/patients/${patientId}?c=${encodeURIComponent(conversationId)}`;
}

/** Passed through location state so the chart URL stays `/patients/:id?tab=…`. */
export type CreateTreatmentPlanLocationState = {
  createTreatmentPlan: true;
  toothNumbers?: number[];
};

export type CreateLabOrderLocationState = { createLabOrder: true };
export type CreatePrescriptionLocationState = { createPrescription: true };
export type RecordPaymentLocationState = { recordPayment: true };

/** Choose which plan the treatments tabs show after a navigation that must not touch the URL. */
export type SelectTreatmentPlanLocationState = {
  selectedTreatmentPlanId: string;
};

export type PatientChartLocationState =
  | CreateTreatmentPlanLocationState
  | CreateLabOrderLocationState
  | CreatePrescriptionLocationState
  | RecordPaymentLocationState
  | SelectTreatmentPlanLocationState;

export type TreatmentPlanDetailPathOptions = {
  /** Plan detail sub-tab (`overview` | `treatment` | `payment`); omit for default. */
  planTab?: string | null;
};

/** Canonical plan record. Opened from the patient chart without nesting under `/patients`. */
export function buildPatientTreatmentPlanDetailPath(
  _patientId: string,
  planId: string,
  options: TreatmentPlanDetailPathOptions = {}
): string {
  if (!options.planTab || options.planTab === 'overview') {
    return `/treatment-plans/${planId}`;
  }
  return `/treatment-plans/${planId}?tab=${options.planTab}`;
}
